if (!window.CusaShelterChallengeVotingButton) {
	/**
	 * Custom HTML element for voting on shelter challenges.
	 *
	 * Responsibilities:
	 * - Determines if the user has already voted today (PST timezone).
	 * - Reads and validates data attributes (`data-shelter-id`, `data-shelter-challenge-id`, `data-shelter-challenge-handle`) on connect.
	 * - Enables or disables voting buttons across the page based on eligibility.
	 * - Submits a vote via POST request and handles success/error responses.
	 * - Displays toast notifications for user feedback and request state.
	 * - Manages UI states including button spinners, disabled states, and redirect to thank-you page.
	 * - Stores a local lockout in localStorage to prevent repeat votes in a single day.
	 * - Cleans up event listeners on disconnect.
	 *
	 * Requirements:
	 * - Must include a button inside the element.
	 * - Requires `data-shelter-id`, `data-shelter-challenge-id`, and `data-shelter-challenge-handle` attributes on the custom element.
	 */
	class CusaShelterChallengeVotingButton extends HTMLElement {
		/**
		 * Creates an instance of CusaShelterChallengeVotingButton and initializes properties.
		 */
		constructor() {
			super();
			/** @type {HTMLButtonElement} */
			this.voteBtn = this.querySelector('button');
			/** @type {NodeListOf<HTMLButtonElement>} */
			this.allVoteBtns = document.querySelectorAll('cusa-shelter-challenge-voting-button button');
			/** @type {HTMLButtonElement | null} */
			this.lastClickedBtn = null;
			/** @type {string} */
			this.storageKey = ''; // dynamically set later
			/** @type {string | null} */
			this.reqStatusToast = null;
			/** @type {string | null} */
			this.errorMsg = null;
		}
		/**
		 * Called when the element is inserted into the DOM. Sets up event listeners and initializes button state.
		 */
		connectedCallback() {
			this.shelterId = this.dataset.shelterId;
			this.shelterChallengeId = this.dataset.shelterChallengeId;
			this.shelterChallengeHandle = this.dataset.shelterChallengeHandle;
			this.shelterIdentifierString = `${this.shelterChallengeHandle}_${this.shelterChallengeId}`;
			this.storageKey = `vote_${this.shelterIdentifierString}`;
			this.addEventListener('click', this.handleBtnClick, false);

			if (!this.hasVotedToday()) {
				if (
					this.shelterId.length &&
					this.shelterChallengeId.length &&
					this.shelterChallengeHandle.length &&
					this.shelterIdentifierString.length &&
					this.storageKey.includes(this.shelterIdentifierString)
				) {
					this.voteBtn.removeAttribute('disabled');
					this.voteBtn.classList.remove('disabled');
				}
			}
		}
		/**
		 * Called when the element is removed from the DOM. Cleans up event listeners.
		 */
		disconnectedCallback() {
			this.removeEventListener('click', this.handleBtnClick, false);
		}

		/**
		 * Returns today's date in PST timezone in YYYY-MM-DD format.
		 * @returns {string} Today's date in PST (YYYY-MM-DD)
		 */
		getTodayInPST = () => {
			const now = new Date();
			const pstDate = new Intl.DateTimeFormat('en-US', {
				timeZone: 'America/Los_Angeles',
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			}).format(now);
			const [month, day, year] = pstDate.split('/');
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
		};

		/**
		 * Checks if the user has already voted today for the given shelter challenge.
		 * @returns {boolean} True if already voted today, false otherwise.
		 */
		hasVotedToday = () => {
			const record = getLocalStorage(this.storageKey);
			if (!record) return false;

			const { date } = JSON.parse(record);
			const today = this.getTodayInPST();
			return date === today;
		};

		/**
		 * Sets a localStorage lock to indicate a vote has been cast today for the given shelter challenge.
		 */
		setLocalVoteLock = () => {
			const today = this.getTodayInPST();
			setLocalStorage(this.storageKey, JSON.stringify({ date: today }));
		};

		/**
		 * Handles the click event for the vote button. Manages voting logic and UI feedback.
		 * @param {Event} e The click event object.
		 */
		handleBtnClick = async (e) => {
			this.lastClickedBtn = e.target.closest('button');
			this.allVoteBtns = document.querySelectorAll('cusa-shelter-challenge-voting-button button');
			if (this.hasVotedToday()) {
				CusaToastNotification.create({
					message: "You've already voted for a shelter today. Please come back tomorrow!",
					type: 'info',
					duration: '3000',
					title: 'Vote already counted'
				});
				this.disableVoteBtns(e);
				return;
			}
			this.reqStatusToast && CusaToastNotification.dismiss(this.reqStatusToast);
			CusaToastNotification.clearAll();
			const waitMsg = setTimeout(() => {
				this.reqStatusToast = CusaToastNotification.create({
					message: 'Please remain on the page while we record your vote…',
					type: 'info',
					duration: 'infinite'
				});
			}, 500);
			this.disableVoteBtns(e);

			const fetchBody = {
				challenge_id: this.shelterChallengeId,
				shelter_id: this.shelterId
			};

			try {
				const { response, data } = await cusaFetch(CUSA_ENDPOINTS.shelterVote, 'POST', JSON.stringify(fetchBody));

				console.log('Response:', response);
				console.log('Data:', data);

				if (response.ok) {
					clearTimeout(waitMsg);
					logFetchSuccess(
						response.status,
						`Shelter ID #${this.shelterId}`,
						`Vote counted. Total votes: ${data.total_votes}`
					);
					CusaToastNotification.dismiss(this.reqStatusToast);
					// CusaToastNotification.create({
					// 	message: 'Thank you! Your vote has been counted!',
					// 	type: 'success',
					// 	duration: 'infinite',
					// 	title: 'Success!'
					// });
					this.setLocalVoteLock(this.shelterIdentifierString);
					this.disableVoteBtns(new Event('click'));

					if (this.lastClickedBtn) {
						this.removeSpinnerState(this.lastClickedBtn);
					}

					setTimeout(() => {
						window.location.href = this.dataset.thankYouUrl;
					}, 100);
				}
			} catch (error) {
				CusaToastNotification.dismiss(this.reqStatusToast);
				this.errorMsg = CusaToastNotification.create({
					message: 'We encountered an error recording your vote. Please try again in a few minutes.',
					type: 'error',
					duration: 'infinite',
					title: 'Uh oh…'
				});
				console.warn('Rejected Response:', error);
				this.enableVoteBtns();
			}
		};

		/**
		 * Disables a passed in button and shows the loading spinner.
		 * @param {HTMLElement} btn A button element to disable.
		 * @param {Event} e The event object associated with the button click.
		 */
		addSpinnerState = (btn, e) => {
			btn.disabled = true;
			if (btn === e.target) {
				btn.querySelector('span').style.opacity = 0;
				btn.classList.add('button--secondary');
				btn.querySelector('.loading__spinner').classList.remove('hidden');
			}
		};

		/**
		 * Removes the spinner state from a passed in button and optionally enables it.
		 * @param {HTMLElement} btn A button element to update.
		 * @param {boolean} enableBool Whether to enable the button after removing spinner.
		 */
		removeSpinnerState = (btn, enableBool) => {
			if (enableBool) {
				btn.removeAttribute('disabled');
			}
			btn.querySelector('span').style.opacity = 1;
			btn.classList.remove('button--secondary');
			btn.querySelector('.loading__spinner').classList.add('hidden');
		};

		/**
		 * Loops through all vote buttons on the page and disables them using the 'addSpinnerState' function.
		 * @param {Event} e The event object associated with the button click.
		 */
		disableVoteBtns = (e) => {
			for (const btn of this.allVoteBtns) {
				this.addSpinnerState(btn, e);
			}
		};

		/**
		 * Loops through all vote buttons on the page and enables them using the 'removeSpinnerState' function.
		 */
		enableVoteBtns = () => {
			for (const btn of this.allVoteBtns) {
				this.removeSpinnerState(btn, true);
			}
		};
	}

	window.CusaShelterChallengeVotingButton = CusaShelterChallengeVotingButton;

	// Prevent "already defined" error during local dev with Shopify CLI hot reload.
	if (!customElements.get('cusa-shelter-challenge-voting-button')) {
		customElements.define('cusa-shelter-challenge-voting-button', CusaShelterChallengeVotingButton);
	}
}
